import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import QmsJsonImport from '../components/QmsJsonImport';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#f472b6';
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || '#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}
function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
const SUPPLIER_ROLES = ['gpv','ubiqconn','acmepoint','hk_ringer'];

export default function BomList() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const isStaff = ['admin','quality-manager','production-engineer','service'].includes(role);
  const isSupplier = SUPPLIER_ROLES.includes(String(role||'').replace(/^test-/,''));
  const canEdit = isStaff || isSupplier;

  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/boms`, {
        headers:{ Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load BOMs');
      if (data.ok) setBoms(data.boms || []);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const term = search.trim().toLowerCase();
  const visible = term
    ? boms.filter(b =>
        [b.documentNumber, b.name, b.productFamily].some(v => String(v||'').toLowerCase().includes(term)))
    : boms;

  const fs = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'6px 10px',fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,flexWrap:'wrap',gap:10 }}>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>Bills of Materials</h2>
        {canEdit && (
          <button onClick={()=>navigate('/quality/boms/new')}
            style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
              padding:'8px 18px',fontWeight:700,fontSize:13,cursor:'pointer' }}>+ New BOM</button>
        )}
      </div>
      <p style={{ margin:'0 0 18px',color:'var(--ni-text-muted)',fontSize:13 }}>
        Author and review BOMs in QMS, with pinned assembly and test references. SharePoint holds secondary exported copies.
      </p>

      {isStaff && <QmsJsonImport kind="bom" onCreated={load} />}
      {error && <p role="alert" style={{ color:'#EC2124' }}>{error}</p>}

      <div style={{ display:'flex',gap:10,marginBottom:16,flexWrap:'wrap' }}>
        <input placeholder="Search doc no., name, family…" value={search}
          onChange={e=>setSearch(e.target.value)} style={{ ...fs,minWidth:240 }} />
      </div>

      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,overflow:'hidden' }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>
        : visible.length===0 ? (
          <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>
            No BOMs found.{' '}
            {canEdit && <button onClick={()=>navigate('/quality/boms/new')}
              style={{ background:'none',border:'none',color:ACCENT,cursor:'pointer',fontSize:14 }}>Create one.</button>}
          </div>
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)',fontSize:11,textTransform:'uppercase',
                letterSpacing:'0.06em',background:'rgba(0,0,0,0.2)' }}>
                {['Doc No.','Name','Product Family','Rev.','Status','Items','Updated'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px',textAlign:'left',fontWeight:600,whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(b=>(
                <tr key={b._id} onClick={()=>navigate(`/quality/boms/${b._id}`)}
                  style={{ cursor:'pointer',borderTop:'1px solid var(--ni-border-soft)' }}
                  onMouseEnter={ev=>ev.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'11px 16px',fontFamily:'monospace',fontSize:12,color:ACCENT,whiteSpace:'nowrap' }}>{b.documentNumber}</td>
                  <td style={{ padding:'11px 16px',fontWeight:600 }}>{b.name}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{b.productFamily||'—'}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',fontFamily:'monospace' }}>{b.currentRevision}</td>
                  <td style={{ padding:'11px 16px',whiteSpace:'nowrap' }}><StatusBadge status={b.currentStatus}/></td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{b.itemCount}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                    {b.updatedAt?new Date(b.updatedAt).toLocaleDateString():'—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10,color:'var(--ni-text-muted)',fontSize:12 }}>{visible.length} BOM{visible.length!==1?'s':''}</div>
    </div>
  );
}
